import { Box, StatusIndicator, StatusIndicatorProps } from '@amzn/awsui-components-react'
import { readString } from 'react-papaparse'
import {
    ALLOCATION_WEEK_COLUMN,
    CheckResult,
    ColumnDataType,
    COOP_COLUMN,
    DECIMAL_PATTERN,
    EMPLOYEE_COLUMN,
    EXPECTED_HEADERS,
    formatLineLocation,
    FRIDAY_DAY_INDEX,
    HR_VARIANCE_REASON_OTHER,
    HrVariance,
    HrVarianceReasonRequest,
    hrVarianceKey,
    ImportErrorRow,
    RowValidationError,
    RowValidationResult,
    TEMPLATE_COLUMNS,
    TEMPLATE_EXAMPLE_ROWS,
    VarianceReasonState,
} from './ImportAllocationPageConstants'

export const checkResultToStatusType = (result: CheckResult): StatusIndicatorProps.Type =>
    result === CheckResult.PASS ? 'success' : 'error'

export interface SummaryStatus {
    type: StatusIndicatorProps.Type
    text: string
}

export const SummaryStatusField = ({ label, status }: { label: string; status: SummaryStatus }) => (
    <div>
        <Box variant='awsui-key-label'>{label}</Box>
        <StatusIndicator type={status.type}>{status.text}</StatusIndicator>
    </div>
)

const escapeCsvField = (value: string): string =>
    /[",\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value

const excelTextGuard = (value: string): string => (value.trim().length > 0 ? `'${value}` : value)

const ALLOCATION_WEEK_INDEX = EXPECTED_HEADERS.indexOf(ALLOCATION_WEEK_COLUMN)

export const sanitizeUploadedCsvText = (text: string): string => {
    let rows: string[][] = []
    readString<string[]>(text, {
        skipEmptyLines: false,
        complete: (results: { data: string[][] }) => {
            rows = results.data
        },
    })
    const cleaned = rows.map((row, index) =>
        index === 0
            ? row
            : row.map((cell, columnIndex) =>
                  columnIndex === ALLOCATION_WEEK_INDEX ? cell.replace(/^'/, '') : cell,
              ),
    )
    return cleaned.map((row) => row.map(escapeCsvField).join(',')).join('\n')
}

const timestampForFileName = (date: Date): string => {
    const pad = (value: number) => String(value).padStart(2, '0')
    const datePart = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
    const timePart = `${pad(date.getHours())}-${pad(date.getMinutes())}-${pad(date.getSeconds())}`
    return `${datePart}_${timePart}`
}
const downloadCsv = (csv: string, fileName: string): void => {
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = fileName
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
}

export const downloadTemplateCsv = (): void => {
    const headerLine = EXPECTED_HEADERS.map(escapeCsvField).join(',')
    const exampleLines = TEMPLATE_EXAMPLE_ROWS.map((row) =>
        EXPECTED_HEADERS.map((header) => escapeCsvField(row[header] ?? '')).join(','),
    )
    downloadCsv(
        `${[headerLine, ...exampleLines].join('\n')}\n`,
        `falcon-allocation-template-${timestampForFileName(new Date())}.csv`,
    )
}

export const downloadAnnotatedCsv = (
    dataRows: string[][],
    results: RowValidationResult[],
): void => {
    const resultByRow = new Map(results.map((result) => [result.rowNumber, result]))
    const headerLine = [...EXPECTED_HEADERS, 'Validation Status', 'Validation Reason']
        .map(escapeCsvField)
        .join(',')
    const bodyLines = dataRows.map((row, rowIndex) => {
        const result = resultByRow.get(rowIndex + 1)
        const status = result?.result === CheckResult.FAIL ? 'Failed' : 'Passed'
        const reason = result?.details ?? ''
        const cells = EXPECTED_HEADERS.map((_header, columnIndex) => {
            const value = row[columnIndex] ?? ''
            return columnIndex === ALLOCATION_WEEK_INDEX ? excelTextGuard(value) : value
        })
        return [...cells, status, reason].map(escapeCsvField).join(',')
    })
    downloadCsv(
        `${[headerLine, ...bodyLines].join('\n')}\n`,
        `falcon-allocation-validation-${timestampForFileName(new Date())}.csv`,
    )
}

export const formatCountLabel = (count: number): string | undefined =>
    count > 0 ? `(${count})` : undefined

export const parseAllocationDate = (value: string): Date | null => {
    const trimmed = value.trim()
    const iso = trimmed.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/)
    const mdy = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})$/)
    let year: number
    let month: number
    let day: number
    if (iso) {
        year = Number(iso[1])
        month = Number(iso[2])
        day = Number(iso[3])
    } else if (mdy) {
        month = Number(mdy[1])
        day = Number(mdy[2])
        const rawYear = Number(mdy[3])
        // Match Python strptime %y: 00-68 -> 2000-2068, 69-99 -> 1969-1999.
        year = mdy[3].length === 2 ? (rawYear <= 68 ? 2000 + rawYear : 1900 + rawYear) : rawYear
    } else {
        return null
    }
    const date = new Date(year, month - 1, day)
    if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
        return null
    }
    return date
}

const matchesDataType = (column: (typeof TEMPLATE_COLUMNS)[number], value: string): boolean => {
    const trimmed = value.trim()
    if (trimmed.length === 0) {
        return true
    }
    if (column.dataType === ColumnDataType.DECIMAL) {
        return DECIMAL_PATTERN.test(trimmed)
    }
    if (column.id === ALLOCATION_WEEK_COLUMN) {
        const date = parseAllocationDate(trimmed)
        return date !== null && date.getDay() === FRIDAY_DAY_INDEX
    }
    return true
}

export const validateRequiredFields = (dataRows: string[][]): RowValidationError[] => {
    const errors: RowValidationError[] = []
    dataRows.forEach((row, rowIndex) => {
        const rowNumber = rowIndex + 1
        TEMPLATE_COLUMNS.forEach((column, columnIndex) => {
            const value = (row[columnIndex] ?? '').trim()
            if (column.required && value.length === 0) {
                errors.push({
                    rowNumber,
                    column: column.id,
                    value,
                    reason: 'Required field is empty.',
                })
            }
        })
    })
    return errors
}

// Employee and Co-op are each optional on their own, but a row is invalid when both are blank.
const EMPLOYEE_INDEX = EXPECTED_HEADERS.indexOf(EMPLOYEE_COLUMN)
const COOP_INDEX = EXPECTED_HEADERS.indexOf(COOP_COLUMN)

export const validateEmployeeOrCoop = (dataRows: string[][]): RowValidationError[] => {
    const errors: RowValidationError[] = []
    dataRows.forEach((row, rowIndex) => {
        const employee = (row[EMPLOYEE_INDEX] ?? '').trim()
        const coop = (row[COOP_INDEX] ?? '').trim()
        if (employee.length === 0 && coop.length === 0) {
            errors.push({
                rowNumber: rowIndex + 1,
                column: `${EMPLOYEE_COLUMN} / ${COOP_COLUMN}`,
                value: '',
                reason: `Provide a value for ${EMPLOYEE_COLUMN} or ${COOP_COLUMN}. At least one is required.`,
            })
        }
    })
    return errors
}

export const validateDataTypes = (dataRows: string[][]): RowValidationError[] => {
    const errors: RowValidationError[] = []
    dataRows.forEach((row, rowIndex) => {
        const rowNumber = rowIndex + 1
        TEMPLATE_COLUMNS.forEach((column, columnIndex) => {
            const value = (row[columnIndex] ?? '').trim()
            if (!matchesDataType(column, value)) {
                errors.push({
                    rowNumber,
                    column: column.id,
                    value,
                    reason: `Value does not match the expected type: ${column.typeLabel}.`,
                })
            }
        })
    })
    return errors
}

const errorLineNumbers = (error: {
    row_number: number | null
    row_numbers: number[] | null
}): number[] => {
    const lines = new Set<number>()
    if (error.row_number !== null) {
        lines.add(error.row_number)
    }
    if (error.row_numbers) {
        error.row_numbers.forEach((line) => lines.add(line))
    }
    return Array.from(lines).sort((a, b) => a - b)
}

export const buildImportErrorRows = (
    errors: {
        row_number: number | null
        row_numbers: number[] | null
        column: string | null
        message: string
    }[],
): ImportErrorRow[] => {
    const byLocation = new Map<string, ImportErrorRow>()
    errors.forEach((error) => {
        const lineNumbers = errorLineNumbers(error)
        const key = lineNumbers.length === 0 ? 'file-level' : lineNumbers.join(',')
        const location = formatLineLocation(lineNumbers)
        const failure = { column: error.column, message: error.message }
        const existing = byLocation.get(key)
        if (existing) {
            existing.failures.push(failure)
        } else {
            byLocation.set(key, {
                lineNumbers,
                sortKey: lineNumbers.length === 0 ? -1 : lineNumbers[0],
                location,
                failures: [failure],
            })
        }
    })
    return Array.from(byLocation.values()).sort((a, b) => a.sortKey - b.sortKey)
}

const formatErrorRowDetails = (row: ImportErrorRow): string =>
    row.failures
        .map((failure) =>
            failure.column ? `${failure.column}: ${failure.message}` : failure.message,
        )
        .join('\n')

export const downloadUploadedCsvWithErrors = (csvText: string, errorRows: ImportErrorRow[]): void => {
    const detailByLine = new Map<number, string[]>()
    const fileLevelDetails: string[] = []
    errorRows.forEach((row) => {
        const detail = formatErrorRowDetails(row)
        if (row.lineNumbers.length === 0) {
            fileLevelDetails.push(detail)
            return
        }
        row.lineNumbers.forEach((lineNumber) => {
            const existing = detailByLine.get(lineNumber) ?? []
            existing.push(detail)
            detailByLine.set(lineNumber, existing)
        })
    })
    const fileLevelDetail = fileLevelDetails.join('\n')

    let rows: string[][] = []
    readString<string[]>(csvText, {
        skipEmptyLines: false,
        complete: (results: { data: string[][] }) => {
            rows = results.data
        },
    })
    while (rows.length > 0 && rows[rows.length - 1].every((cell) => cell === '')) {
        rows.pop()
    }

    const annotated = rows.map((row, index) => {
        if (index === 0) {
            const header = fileLevelDetail
                ? `Import Errors (file-level: ${fileLevelDetail})`
                : 'Import Errors'
            return [...row, header].map(escapeCsvField).join(',')
        }
        const guarded = row.map((cell, columnIndex) =>
            columnIndex === ALLOCATION_WEEK_INDEX ? excelTextGuard(cell) : cell,
        )
        const details = detailByLine.get(index + 1)
        return [...guarded, details ? details.join('\n') : ''].map(escapeCsvField).join(',')
    })
    downloadCsv(
        `${annotated.join('\n')}\n`,
        `falcon-allocation-import-errors-${timestampForFileName(new Date())}.csv`,
    )
}

export const buildRowValidationResults = (
    rowCount: number,
    errors: RowValidationError[],
): RowValidationResult[] => {
    const errorsByRow = new Map<number, RowValidationError[]>()
    errors.forEach((error) => {
        const existing = errorsByRow.get(error.rowNumber) ?? []
        existing.push(error)
        errorsByRow.set(error.rowNumber, existing)
    })
    return Array.from({ length: rowCount }, (_unused, rowIndex) => {
        const rowNumber = rowIndex + 1
        const rowErrors = errorsByRow.get(rowNumber) ?? []
        return {
            rowNumber,
            result: rowErrors.length === 0 ? CheckResult.PASS : CheckResult.FAIL,
            failures: rowErrors.map((error) => ({ column: error.column, reason: error.reason })),
            details: rowErrors.map((error) => `${error.column}: ${error.reason}`).join('\n'),
        }
    })
}

export const isVarianceReasonComplete = (state: VarianceReasonState | undefined): boolean => {
    if (!state?.reason) {
        return false
    }
    if (state.reason.value === HR_VARIANCE_REASON_OTHER) {
        return state.note.trim().length > 0
    }
    return true
}

export const incompleteVarianceKeys = (
    variances: HrVariance[],
    reasonStateByKey: Record<string, VarianceReasonState>,
): string[] =>
    variances
        .map((variance) => hrVarianceKey(variance.team_id, variance.allocation_week))
        .filter((key) => !isVarianceReasonComplete(reasonStateByKey[key]))

export const buildVarianceReasonNote = (state: VarianceReasonState | undefined): string => {
    const label = state?.reason?.label ?? ''
    const note = state?.note.trim() ?? ''
    if (!state?.reason) {
        return ''
    }
    if (state.reason.value === HR_VARIANCE_REASON_OTHER) {
        return note
    }
    return note ? `${label} — ${note}` : label
}

export const buildHrVarianceReasons = (
    variances: HrVariance[],
    reasonStateByKey: Record<string, VarianceReasonState>,
): HrVarianceReasonRequest[] =>
    variances.map((variance) => ({
        team_id: variance.team_id,
        allocation_week: variance.allocation_week,
        hr_detail_note: buildVarianceReasonNote(
            reasonStateByKey[hrVarianceKey(variance.team_id, variance.allocation_week)],
        ),
    }))

const canonicalWeek = (value: string): string | null => {
    const trimmed = value.trim()
    const iso = trimmed.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/)
    if (iso) {
        return `${Number(iso[1])}-${Number(iso[2])}-${Number(iso[3])}`
    }
    const date = parseAllocationDate(trimmed)
    return date ? `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}` : null
}

const varianceMatchKey = (org: string, group: string, team: string, week: string): string =>
    `${org.trim()}||${group.trim()}||${team.trim()}||${week}`

export const writeReasonsIntoComments = (
    fileText: string,
    variances: HrVariance[],
    reasonStateByKey: Record<string, VarianceReasonState>,
): string => {
    const noteByKey = new Map<string, string>()
    variances.forEach((variance) => {
        const note = buildVarianceReasonNote(
            reasonStateByKey[hrVarianceKey(variance.team_id, variance.allocation_week)],
        )
        const week = canonicalWeek(variance.allocation_week)
        if (note && week !== null) {
            noteByKey.set(
                varianceMatchKey(variance.org_name, variance.group_name, variance.team_name, week),
                note,
            )
        }
    })

    const commentIndex = EXPECTED_HEADERS.indexOf('Comment')
    const weekIndex = EXPECTED_HEADERS.indexOf(ALLOCATION_WEEK_COLUMN)
    const orgIndex = EXPECTED_HEADERS.indexOf('Organization')
    const groupIndex = EXPECTED_HEADERS.indexOf('Group')
    const teamIndex = EXPECTED_HEADERS.indexOf('Team')

    let rows: string[][] = []
    readString<string[]>(fileText, {
        skipEmptyLines: false,
        complete: (results: { data: string[][] }) => {
            rows = results.data
        },
    })
    const annotated = rows.map((row, index) => {
        if (index === 0 || row.length <= 1) {
            return row
        }
        const week = canonicalWeek(row[weekIndex] ?? '')
        if (week === null) {
            return row
        }
        const note = noteByKey.get(
            varianceMatchKey(
                row[orgIndex] ?? '',
                row[groupIndex] ?? '',
                row[teamIndex] ?? '',
                week,
            ),
        )
        if (note === undefined) {
            return row
        }
        const updated = [...row]
        updated[commentIndex] = note
        return updated
    })
    return annotated.map((row) => row.map(escapeCsvField).join(',')).join('\n')
}

export const encodeBase64Utf8 = (text: string): string => {
    const bytes = new TextEncoder().encode(text)
    let binary = ''
    bytes.forEach((byte) => {
        binary += String.fromCharCode(byte)
    })
    return btoa(binary)
}

export const downloadSubmittedCsv = (csvText: string, originalName: string): void => {
    const baseName = originalName.replace(/\.csv$/i, '') || 'falcon-allocation'
    downloadCsv(`${csvText}\n`, `${baseName}-submitted-${timestampForFileName(new Date())}.csv`)
}
